import React, { useState, useEffect } from 'react';
import {
  getRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
} from '../../adminApi';
import { RoutingRule } from '../../adminTypes';

type WorkflowType = 'US_FOODS' | 'GFS' | 'STAPLES' | 'OTHER' | '';

const WORKFLOW_TYPE_LABELS: Record<string, string> = {
  US_FOODS: 'Special Processing - US Foods',
  GFS: 'Special Processing - GFS',
  STAPLES: 'Special Processing - Staples',
  OTHER: 'Standard',
};
const getWorkflowTypeLabel = (type: string) => WORKFLOW_TYPE_LABELS[type] || type;
type DocumentType = 'INVOICE' | 'CREDIT_MEMO' | 'STATEMENT' | '';

interface FormData {
  rule_name: string;
  priority: number;
  match_vendor_number: string;
  match_workflow_type: WorkflowType;
  match_job_number: string;
  match_document_type: DocumentType;
  match_amount_min: string;
  match_amount_max: string;
  route_to_approver_id: string;
  auto_approve: boolean;
  escalation_hours: number;
  escalate_to_approver_id: string;
  active: boolean;
}

const initialFormData: FormData = {
  rule_name: '',
  priority: 100,
  match_vendor_number: '',
  match_workflow_type: '',
  match_job_number: '',
  match_document_type: '',
  match_amount_min: '',
  match_amount_max: '',
  route_to_approver_id: '',
  auto_approve: false,
  escalation_hours: 48,
  escalate_to_approver_id: '',
  active: true,
};

const RoutingRules: React.FC = () => {
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRoutingRules();
      const sorted = data.sort((a, b) => (a.priority || 0) - (b.priority || 0));
      setRules(sorted);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch routing rules');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingRule(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (rule: RoutingRule) => {
    setEditingRule(rule);
    setFormData({
      rule_name: rule.rule_name || '',
      priority: rule.priority || 100,
      match_vendor_number: rule.match_vendor_number ? String(rule.match_vendor_number) : '',
      match_workflow_type: (rule.match_workflow_type || '') as WorkflowType,
      match_job_number: rule.match_job_number || '',
      match_document_type: (rule.match_document_type || '') as DocumentType,
      match_amount_min: rule.match_amount_min ? String(rule.match_amount_min) : '',
      match_amount_max: rule.match_amount_max ? String(rule.match_amount_max) : '',
      route_to_approver_id: rule.route_to_approver_id ? String(rule.route_to_approver_id) : '',
      auto_approve: rule.auto_approve || false,
      escalation_hours: rule.escalation_hours || 48,
      escalate_to_approver_id: rule.escalate_to_approver_id ? String(rule.escalate_to_approver_id) : '',
      active: rule.active !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRule(null);
    setFormData(initialFormData);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.rule_name.trim()) {
      setError('Rule name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        rule_name: formData.rule_name,
        priority: formData.priority,
        match_vendor_number: formData.match_vendor_number ? parseInt(formData.match_vendor_number, 10) : null,
        match_workflow_type: formData.match_workflow_type || null,
        match_job_number: formData.match_job_number || null,
        match_document_type: formData.match_document_type || null,
        match_amount_min: formData.match_amount_min ? parseFloat(formData.match_amount_min) : null,
        match_amount_max: formData.match_amount_max ? parseFloat(formData.match_amount_max) : null,
        route_to_approver_id: formData.route_to_approver_id ? parseInt(formData.route_to_approver_id, 10) : null,
        auto_approve: formData.auto_approve,
        escalation_hours: formData.escalation_hours,
        escalate_to_approver_id: formData.escalate_to_approver_id ? parseInt(formData.escalate_to_approver_id, 10) : null,
        active: formData.active,
      };

      if (editingRule) {
        await updateRoutingRule(editingRule.id, payload);
      } else {
        await createRoutingRule(payload);
      }

      await fetchRules();
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save routing rule');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleteTargetId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;

    try {
      setSubmitting(true);
      setError(null);
      await deleteRoutingRule(deleteTargetId);
      await fetchRules();
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete routing rule');
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmountRange = (min?: number | null, max?: number | null) => {
    if (!min && !max) return <span style={{ color: '#999' }}>Any</span>;
    if (min && max) return `$${min.toFixed(2)} - $${max.toFixed(2)}`;
    if (min) return `$${min.toFixed(2)}+`;
    return `Up to $${max?.toFixed(2)}`;
  };

  const containerStyle: React.CSSProperties = {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid #00386C',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#00386C',
    margin: 0,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#00386C',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  };

  const tableContainerStyle: React.CSSProperties = {
    overflowX: 'auto',
    marginBottom: '20px',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  };

  const thStyle: React.CSSProperties = {
    backgroundColor: '#00386C',
    color: 'white',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #002d54',
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid #eee',
  };

  const rowHoverStyle: React.CSSProperties = {
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
  };

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalCardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  };

  const modalHeaderStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#00386C',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #00386C',
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
  };

  const checkboxStyle: React.CSSProperties = {
    marginRight: '8px',
  };

  const checkboxLabelStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
  };

  const formRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  };

  const modalButtonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '12px',
    borderTop: '1px solid #eee',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#666',
  };

  const deleteButtonStyle: React.CSSProperties = {
    backgroundColor: '#d9534f',
    color: 'white',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  };

  const confirmDialogOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
  };

  const confirmDialogStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '400px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    color: '#721c24',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
    fontSize: '14px',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <p>Loading routing rules...</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Approval Routing Rules</h1>
        <button style={buttonStyle} onClick={openAddModal}>
          + Add Rule
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Priority</th>
              <th style={thStyle}>Rule Name</th>
              <th style={thStyle}>Vendor</th>
              <th style={thStyle}>Workflow</th>
              <th style={thStyle}>Job</th>
              <th style={thStyle}>Amount Range</th>
              <th style={thStyle}>Route To</th>
              <th style={thStyle}>Auto-Approve</th>
              <th style={thStyle}>Escalation</th>
              <th style={thStyle}>Active</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr>
                <td style={{ ...tdStyle, textAlign: 'center', color: '#999' }} colSpan={11}>
                  No routing rules configured
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr
                  key={rule.id}
                  style={rowHoverStyle}
                  onClick={() => openEditModal(rule)}
                >
                  <td style={tdStyle}>{rule.priority || '—'}</td>
                  <td style={tdStyle}>{rule.rule_name}</td>
                  <td style={tdStyle}>
                    {rule.match_vendor_number ? (
                      `#${rule.match_vendor_number}`
                    ) : (
                      <span style={{ color: '#999' }}>Any</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {rule.match_workflow_type ? (
                      getWorkflowTypeLabel(rule.match_workflow_type)
                    ) : (
                      <span style={{ color: '#999' }}>Any</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {rule.match_job_number ? (
                      rule.match_job_number
                    ) : (
                      <span style={{ color: '#999' }}>Any</span>
                    )}
                  </td>
                  <td style={tdStyle}>{formatAmountRange(rule.match_amount_min, rule.match_amount_max)}</td>
                  <td style={tdStyle}>
                    {rule.route_to_approver_id ? `Approver #${rule.route_to_approver_id}` : '—'}
                  </td>
                  <td style={tdStyle}>{rule.auto_approve ? '✓' : '—'}</td>
                  <td style={tdStyle}>
                    {rule.escalation_hours ? `${rule.escalation_hours}h` : '—'}
                  </td>
                  <td style={tdStyle}>{rule.active !== false ? '✓' : '✗'}</td>
                  <td
                    style={tdStyle}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      style={deleteButtonStyle}
                      onClick={() => handleDelete(rule.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={modalOverlayStyle} onClick={closeModal}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              {editingRule ? 'Edit Routing Rule' : 'Add New Routing Rule'}
            </div>

            {error && <div style={errorStyle}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Rule Name *</label>
                <input
                  type="text"
                  name="rule_name"
                  value={formData.rule_name}
                  onChange={handleFormChange}
                  style={inputStyle}
                  placeholder="e.g., Staples Under $500"
                  required
                />
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Priority</label>
                  <input
                    type="number"
                    name="priority"
                    value={formData.priority}
                    onChange={handleFormChange}
                    style={inputStyle}
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Escalation Hours</label>
                  <input
                    type="number"
                    name="escalation_hours"
                    value={formData.escalation_hours}
                    onChange={handleFormChange}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Vendor Number (optional)</label>
                <input
                  type="number"
                  name="match_vendor_number"
                  value={formData.match_vendor_number}
                  onChange={handleFormChange}
                  style={inputStyle}
                  placeholder="e.g., 12345"
                />
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Workflow Type (optional)</label>
                  <select
                    name="match_workflow_type"
                    value={formData.match_workflow_type}
                    onChange={handleFormChange}
                    style={inputStyle}
                  >
                    <option value="">Any</option>
                    <option value="US_FOODS">Special Processing - US Foods</option>
                    <option value="GFS">Special Processing - GFS</option>
                    <option value="STAPLES">Special Processing - Staples</option>
                    <option value="OTHER">Standard</option>
                  </select>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Document Type (optional)</label>
                  <select
                    name="match_document_type"
                    value={formData.match_document_type}
                    onChange={handleFormChange}
                    style={inputStyle}
                  >
                    <option value="">Any</option>
                    <option value="INVOICE">INVOICE</option>
                    <option value="CREDIT_MEMO">CREDIT_MEMO</option>
                    <option value="STATEMENT">STATEMENT</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Job Number (optional)</label>
                <input
                  type="text"
                  name="match_job_number"
                  value={formData.match_job_number}
                  onChange={handleFormChange}
                  style={inputStyle}
                  placeholder="e.g., J-2024-001"
                />
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Min Amount (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="match_amount_min"
                    value={formData.match_amount_min}
                    onChange={handleFormChange}
                    style={inputStyle}
                    placeholder="0.00"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Max Amount (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="match_amount_max"
                    value={formData.match_amount_max}
                    onChange={handleFormChange}
                    style={inputStyle}
                    placeholder="999999.99"
                  />
                </div>
              </div>

              <div style={formRowStyle}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Route To Approver ID (optional)</label>
                  <input
                    type="number"
                    name="route_to_approver_id"
                    value={formData.route_to_approver_id}
                    onChange={handleFormChange}
                    style={inputStyle}
                    placeholder="e.g., 5"
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Escalate To Approver ID (optional)</label>
                  <input
                    type="number"
                    name="escalate_to_approver_id"
                    value={formData.escalate_to_approver_id}
                    onChange={handleFormChange}
                    style={inputStyle}
                    placeholder="e.g., 3"
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    name="auto_approve"
                    checked={formData.auto_approve}
                    onChange={handleFormChange}
                    style={checkboxStyle}
                  />
                  Auto-Approve
                </label>
              </div>

              <div style={formGroupStyle}>
                <label style={checkboxLabelStyle}>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleFormChange}
                    style={checkboxStyle}
                  />
                  Active
                </label>
              </div>

              <div style={modalButtonGroupStyle}>
                <button
                  type="button"
                  style={secondaryButtonStyle}
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={buttonStyle}
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : 'Save Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div style={confirmDialogOverlayStyle} onClick={() => setShowDeleteConfirm(false)}>
          <div style={confirmDialogStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#d9534f', marginTop: 0 }}>Confirm Delete</h3>
            <p>Are you sure you want to delete this routing rule? This action cannot be undone.</p>
            <div style={modalButtonGroupStyle}>
              <button
                style={secondaryButtonStyle}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                style={deleteButtonStyle}
                onClick={confirmDelete}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutingRules;
